import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { getCustomerWishlist } from '../services/wishlistService';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";

  // Auth state
  const [viewMode, setViewMode] = useState("public");
  const [email, setEmail] = useState("");
  const [customerToken, setCustomerToken] = useState(null);
  const [wishlistCount, setWishlistCount] = useState(0);
  const [showLoading, setShowLoading] = useState(false);

  // Session timeout tracking
  const inactivityTimeoutRef = useRef(null);
  const expiryCheckIntervalRef = useRef(null);
  const sessionTimeoutAlertShownRef = useRef(false);
  const isLoggingOutRef = useRef(false);
  const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds

  // Logout handler
  const handleLogout = useCallback(async (isAutoLogout = false) => {
    // Prevent multiple simultaneous logout calls
    if (isLoggingOutRef.current) {
      return;
    }
    isLoggingOutRef.current = true;

    // Note: Confirmation is now handled by the modal in Layout.jsx
    // This function is called after the user confirms in the modal

    // Clear all session and auth data
    sessionStorage.removeItem("perks_email");
    sessionStorage.removeItem("perks_email_verified");
    sessionStorage.removeItem("auto_login_prompted");
    sessionStorage.removeItem("auto_login_prompted_pages");
    sessionStorage.removeItem("auto_login_prompted_this_session");
    localStorage.removeItem("perks_email");
    localStorage.removeItem("perks_email_verified");
    localStorage.removeItem("last_activity");

    // Clear inactivity timeout and reset alert flag
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
      inactivityTimeoutRef.current = null;
    }
    sessionTimeoutAlertShownRef.current = false;

    // Comprehensive cache cleanup to prevent device slowdown
    try {
      // Clear all venue-related caches from sessionStorage
      const sessionKeys = Object.keys(sessionStorage);
      sessionKeys.forEach(key => {
        if (key.startsWith('venue_cache_') ||
            key.startsWith('pending_') ||
            key.startsWith('redirect_') ||
            key.startsWith('company_')) {
          sessionStorage.removeItem(key);
        }
      });

      // Clear any localStorage caches (be selective to preserve non-auth data)
      const localKeys = Object.keys(localStorage);
      localKeys.forEach(key => {
        if (key.startsWith('venue_cache_') ||
            key.startsWith('customer_') ||
            key.startsWith('wishlist_cache_') ||
            key.startsWith('enrollment_')) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error("Error clearing cache during logout:", error);
    }

    // Update state
    setEmail("");
    setCustomerToken(null);
    setWishlistCount(0);
    setViewMode("public");

    // Reset logout flag after a delay to allow state updates to complete
    setTimeout(() => {
      isLoggingOutRef.current = false;
    }, 1000);
  }, []);

  // Fetch customer token
  const fetchCustomerToken = useCallback(async (userEmail) => {
    try {
      const response = await fetch(
        `${apiUrl}/coupon/customer-token?email=${encodeURIComponent(userEmail)}`
      );
      if (response.ok) {
        const data = await response.json();
        const token = data.customer_token;
        setCustomerToken(token);

        try {
          const wishlistData = await getCustomerWishlist(token);
          setWishlistCount(wishlistData.total_count || 0);
        } catch (err) {
          console.error("Failed to fetch wishlist:", err);
        }
      }
    } catch (error) {
      console.error("Error fetching customer token:", error);
    }
  }, [apiUrl]);

  // Function to check and update auth state from storage
  const checkAuthState = useCallback(() => {
    const storedEmail = sessionStorage.getItem("perks_email") || localStorage.getItem("perks_email");
    const verifiedTimestamp = sessionStorage.getItem("perks_email_verified") || localStorage.getItem("perks_email_verified");

    if (storedEmail && verifiedTimestamp) {
      const verifiedDate = new Date(verifiedTimestamp);
      const now = new Date();
      const daysDiff = (now - verifiedDate) / (1000 * 60 * 60 * 24);

      // Check 30-day expiration
      const isWithin30Days = daysDiff < 30;

      // Reset activity timestamp when page is opened/focused
      // This prevents timeout when user closes and reopens the page
      localStorage.setItem("last_activity", new Date().toISOString());

      // Session is valid if within 30 days
      if (isWithin30Days) {
        setEmail(storedEmail);
        setViewMode("resident");

        // Fetch customer token and wishlist
        fetchCustomerToken(storedEmail);
      } else {
        // Clear expired session (only for 30-day expiration)
        handleLogout(true);
      }
    }
  }, [handleLogout, fetchCustomerToken, INACTIVITY_TIMEOUT]);

  // Initialize auth state from storage
  useEffect(() => {
    checkAuthState();
  }, [checkAuthState]);

  // Listen for focus events to re-check auth state when user returns to the tab
  useEffect(() => {
    const handleFocus = () => {
      // DON'T reset alert flag on focus - this causes double alerts
      // The flag should only reset when user logs out or logs back in
      checkAuthState();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [checkAuthState]);

  // Session timeout: Auto-logout after 30 minutes of inactivity
  useEffect(() => {
    if (viewMode !== "resident" || !email) {
      // Only track session for logged-in users
      return;
    }

    const updateLastActivity = () => {
      localStorage.setItem("last_activity", new Date().toISOString());
    };

    const checkSessionExpiry = () => {
      const lastActivity = localStorage.getItem("last_activity");
      if (!lastActivity) {
        updateLastActivity();
        return;
      }

      const lastActivityTime = new Date(lastActivity).getTime();
      const currentTime = new Date().getTime();
      const timeSinceLastActivity = currentTime - lastActivityTime;

      if (timeSinceLastActivity >= INACTIVITY_TIMEOUT) {
        // Session expired - auto logout
        // Don't show alert here - it's already handled by the timeout in resetInactivityTimer
        handleLogout(true);
      }
    };

    const resetInactivityTimer = () => {
      updateLastActivity();

      // Clear existing timeout
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
      }

      // Set new timeout
      inactivityTimeoutRef.current = setTimeout(() => {
        // Clear the interval check to prevent double alert
        if (expiryCheckIntervalRef.current) {
          clearInterval(expiryCheckIntervalRef.current);
          expiryCheckIntervalRef.current = null;
        }

        if (!sessionTimeoutAlertShownRef.current) {
          sessionTimeoutAlertShownRef.current = true;
          alert("Your session has expired due to inactivity. Please log in again.");
        }
        handleLogout(true);
      }, INACTIVITY_TIMEOUT);
    };

    // Activity events to track
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, resetInactivityTimer, true);
    });

    // Initialize timer and set up periodic check
    resetInactivityTimer();

    // Check session expiry every 30 seconds automatically
    expiryCheckIntervalRef.current = setInterval(checkSessionExpiry, 30000);

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, resetInactivityTimer, true);
      });
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
      }
      if (expiryCheckIntervalRef.current) {
        clearInterval(expiryCheckIntervalRef.current);
      }
    };
  }, [viewMode, email, INACTIVITY_TIMEOUT, handleLogout]);

  // Handle login success
  const handleLoginSuccess = useCallback((userEmail, tokenFromModal) => {
    const nowIso = new Date().toISOString();
    sessionStorage.setItem("perks_email", userEmail);
    sessionStorage.setItem("perks_email_verified", nowIso);
    localStorage.setItem("perks_email", userEmail);
    localStorage.setItem("perks_email_verified", nowIso);
    localStorage.setItem("last_activity", nowIso);

    setEmail(userEmail);
    setViewMode("resident");

    if (tokenFromModal) {
      setCustomerToken(tokenFromModal);
    }

    fetchCustomerToken(userEmail);
  }, [fetchCustomerToken]);

  // Update wishlist count
  const updateWishlistCount = useCallback((count) => {
    setWishlistCount(count);
  }, []);

  // Login trigger function (to be set by Layout component)
  const [onLoginTrigger, setOnLoginTrigger] = useState(null);

  const triggerLogin = useCallback(() => {
    if (onLoginTrigger) {
      onLoginTrigger();
    }
  }, [onLoginTrigger]);

  const value = {
    viewMode,
    email,
    customerToken,
    wishlistCount,
    showLoading,
    setShowLoading,
    handleLogout,
    handleLoginSuccess,
    updateWishlistCount,
    setCustomerToken,
    triggerLogin,
    setOnLoginTrigger,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
